import type { FormEvent } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { Textarea } from "app/pages/sales-intelligence/common-components/Textarea/Textarea";

const MIN_NAME_LENGTH = 2;

type SubmitLabelKey = "si.common.button.save" | "si.management.teams.create.name.button.create";

type TeamNameStepProps = {
    name: string;
    description: string;
    existingNames: string[];
    onNameChange: (value: string) => void;
    onDescriptionChange: (value: string | undefined) => void;
    onCancel: () => void;
    onNext: () => void;
    isFinalStep?: boolean;
    isSubmitting?: boolean;
    submitLabelKey?: SubmitLabelKey;
};

export const TeamNameStep = ({
    name,
    description,
    existingNames,
    onNameChange,
    onDescriptionChange,
    onCancel,
    onNext,
    isFinalStep = false,
    isSubmitting = false,
    submitLabelKey = "si.management.teams.create.name.button.create",
}: TeamNameStepProps) => {
    const { translate } = useTranslate();

    const trimmedName = name.trim();
    const hasNameConflict =
        trimmedName.length >= MIN_NAME_LENGTH &&
        existingNames.some((existing) => existing.toLowerCase() === trimmedName.toLowerCase());
    const isCreateDisabled = trimmedName.length < MIN_NAME_LENGTH || hasNameConflict || isSubmitting;

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (isCreateDisabled) {
            return;
        }
        onNext();
    };

    return (
        <Box
            as="form"
            onSubmit={handleSubmit}
            noValidate
            data-automation="create-team-name-form"
        >
            <Modal.Header withBorder>
                <Modal.Title data-automation="create-team-modal-title">
                    {translate("si.management.teams.settings.title")}
                </Modal.Title>
            </Modal.Header>

            <Modal.Content>
                <Stack
                    gap="16px"
                    padding="16px 0px"
                >
                    <Stack
                        gap="8px"
                        data-automation="create-team-name-input"
                    >
                        <Text
                            fontVariant="body1"
                            color={colorsPalettes.carbon["400"]}
                            display="block"
                            as="label"
                            htmlFor="create-team-name-input"
                        >
                            {translate("si.management.teams.create.name.label")}
                        </Text>
                        <TextField
                            autoFocus
                            id="create-team-name-input"
                            maxLength={100}
                            value={name}
                            placeholder={translate("si.management.teams.create.name.placeholder")}
                            onChange={onNameChange}
                            isInvalid={hasNameConflict}
                            errorMessage={translate("si.management.teams.create.name.error.duplicate")}
                        />
                    </Stack>

                    <Stack
                        gap="8px"
                        data-automation="create-team-description-input"
                    >
                        <Text
                            fontVariant="body1"
                            color={colorsPalettes.carbon["400"]}
                            display="block"
                            as="label"
                            htmlFor="create-team-description-textarea"
                        >
                            {translate("si.management.teams.create.name.description.label")}
                        </Text>
                        <Textarea
                            id="create-team-description-textarea"
                            value={description}
                            placeholder={translate("si.management.teams.create.name.description.placeholder")}
                            onChange={onDescriptionChange}
                            width={390}
                        />
                    </Stack>
                </Stack>
            </Modal.Content>

            <Modal.Footer>
                <Flex
                    width="100%"
                    alignItems="center"
                    gap="8px"
                    justifyContent="flex-end"
                >
                    <Button
                        type="flat"
                        buttonHtmlType="button"
                        onClick={onCancel}
                        isDisabled={isSubmitting}
                        dataAutomation="create-team-name-cancel"
                    >
                        {translate("si.common.button.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        buttonHtmlType="submit"
                        isDisabled={isCreateDisabled}
                        isLoading={isFinalStep ? isSubmitting : false}
                        dataAutomation="create-team-name-create"
                    >
                        {translate(submitLabelKey)}
                    </Button>
                </Flex>
            </Modal.Footer>
        </Box>
    );
};
